/**
 * The card, on a phone, on the course.
 *
 * Written for one specific situation and nothing else: somebody standing
 * on a green in the sun, holding a putter in the other hand, on one bar
 * of signal, who wants to put a five on the card and walk to the next
 * tee. Every decision below comes from that.
 *
 * ONE HOLE AT A TIME. Not a grid of eighteen boxes — a grid is a
 * spreadsheet, and a spreadsheet on a phone is eighteen chances to key
 * a number into the wrong row. One hole fills the screen, and the score
 * is chosen by tapping a number rather than typed.
 *
 * SAVED WHEN TAPPED, and the screen says so. Each hole is its own
 * request (0120), so a lost one costs a hole rather than a round, and
 * sending the same one twice is the same as sending it once. A tap that
 * has not landed yet is shown as not landed rather than being hidden
 * behind a spinner over the whole card.
 *
 * AND IT MARKS FOR THE GROUP. Golf's convention is that a playing
 * partner keeps your card, so the tabs across the top are the fourball
 * and one phone can keep all of it. Whose card is being written on is
 * the largest thing on the screen, because entering a six on the wrong
 * person's card is the mistake this design has to make hard.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { api, ApiError } from '../lib/api.ts';
import { useApi } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, ErrorBanner, Loading, Status } from '../components/ui.tsx';
import { Login } from './Login.tsx';
import { LiveBoard } from '../components/LiveBoard.tsx';

interface HoleRow {
  playing_number: number;
  par: number | null;
  stroke_index: number | null;
  strokes_given: number | null;
  strokes: number | null;
  putts: number | null;
}

interface CardPayload {
  score: {
    score_id: string;
    person_id: string;
    player_name: string;
    course_name: string;
    tee_name: string;
    played_on: string;
    holes: number;
    status: string;
    gross: number | null;
    holes_played: number;
    course_handicap: number | null;
    started_hole: number | null;
    marked_by_name: string | null;
    [key: string]: unknown;
  };
  holes: HoleRow[];
  current_hole: number | null;
  playing_with: {
    score_id: string;
    person_id: string;
    player_name: string;
    holes_played: number;
    gross: number | null;
    current_hole: number | null;
  }[];
  competitions: {
    competition_id: string; name: string; format: string; event_name: string;
  }[];
}

/** "+2", "E", "-1" — how a golfer says it. */
function toPar(n: number): string {
  if (n === 0) return 'E';
  return n > 0 ? `+${n}` : String(n);
}

/** Eagle, birdie, par, bogey — for the colour of the tapped number. */
function relative(strokes: number, par: number | null): string {
  if (par === null) return '';
  const d = strokes - par;
  if (d <= -2) return 'hole__score--eagle';
  if (d === -1) return 'hole__score--birdie';
  if (d === 0) return 'hole__score--par';
  if (d === 1) return 'hole__score--bogey';
  return 'hole__score--worse';
}


/* ================================================================== */
/* The card                                                            */
/* ================================================================== */

export function PlayingCardPage() {
  const [scoreId, setScoreId] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<ApiError | null>(null);

  // Whichever card is being written on. Null means "mine", which is what
  // the endpoint returns when nothing is named.
  // `/card`, not the plain score endpoint. That one is a member reading
  // their own record and refuses anybody else's; this one is governed by
  // the marking rule, so a marker can see the card they are writing on.
  const q = useApi<CardPayload>(
    scoreId ? `/v1/golf/scores/${scoreId}/card` : '/v1/golf/card', [scoreId, reload]);

  const mine = useApi<CardPayload>('/v1/golf/card', [reload]);

  const start = async () => {
    setStarting(true);
    setStartError(null);
    try {
      await api('/v1/golf/card', { method: 'POST', body: {} });
      setReload((n) => n + 1);
    } catch (e) {
      setStartError(e as ApiError);
    } finally {
      setStarting(false);
    }
  };

  const d = scoreId ? q.data : mine.data;

  if (mine.loading && !mine.data) return <Card><Loading /></Card>;

  // Nothing open. The one button that matters.
  if (!mine.data?.score) {
    return (
      <Card title="Your Card">
        <ErrorBanner error={mine.error ?? startError} />
        <p className="muted">
          Nothing started today. Scan the sticker on your cart or on the first tee — or
          start one here and the shop&apos;s default course is assumed.
        </p>
        <div className="actions">
          <button className="gold" disabled={starting} onClick={start}>
            Start a card
          </button>
        </div>
      </Card>
    );
  }

  return (
    <CardScreen
      payload={d ?? mine.data}
      myScoreId={mine.data.score.score_id}
      // The group comes from MY round, not from whichever card is on the
      // screen. Read off the card being viewed it includes my own, so
      // the tab strip carried two of me — which React noticed before I
      // did, and which would have let somebody switch to a card by
      // tapping a name that was already selected.
      group={mine.data.playing_with}
      onSwitch={(id) => setScoreId(id === mine.data!.score.score_id ? null : id)}
      onChanged={() => setReload((n) => n + 1)}
    />
  );
}


function CardScreen({
  payload, myScoreId, group, onSwitch, onChanged,
}: {
  payload: CardPayload;
  myScoreId: string;
  group: CardPayload['playing_with'];
  onSwitch: (scoreId: string) => void;
  onChanged: () => void;
}) {
  const card = payload.score;
  const holes = payload.holes;

  // Where to open. The hole they are standing on, not the first — a
  // member picking the phone up at the twelfth should see the twelfth.
  const [hole, setHole] = useState<number>(() => payload.current_hole ?? 1);
  const [pending, setPending] = useState<Record<number, number | null>>({});
  const [failed, setFailed] = useState<Record<number, boolean>>({});
  const [showBoard, setShowBoard] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState<ApiError | null>(null);

  // A different player's card is a different round; go to where THEY are.
  useEffect(() => {
    setHole(payload.current_hole ?? 1);
    setPending({});
    setFailed({});
  }, [card.score_id]);

  const row = holes.find((h) => h.playing_number === hole);
  const played = holes.filter((h) => h.strokes !== null);
  const parSoFar = played.reduce((n, h) => n + (h.par ?? 0), 0);
  const grossSoFar = played.reduce((n, h) => n + (h.strokes ?? 0), 0);

  // What is on the screen for this hole: the tap that has not landed
  // yet, or what the server holds.
  const shown = hole in pending ? pending[hole]! : (row?.strokes ?? null);

  const save = async (strokes: number | null) => {
    setPending((p) => ({ ...p, [hole]: strokes }));
    setFailed((f) => ({ ...f, [hole]: false }));
    try {
      await api(`/v1/golf/scores/${card.score_id}/hole/${hole}`, {
        method: 'PUT', body: { strokes },
      });
      setPending((p) => {
        const next = { ...p };
        delete next[hole];
        return next;
      });
      onChanged();
      // Straight on to the next hole once a score lands, because that is
      // what happens next. Not on the last one — there is nowhere to go.
      if (strokes !== null && hole < (card.holes ?? 18)) {
        setHole((h) => Math.min(h + 1, card.holes ?? 18));
      }
    } catch {
      // Kept on the screen and marked, rather than reverted. The member
      // took that score; the phone just has not managed to say so yet.
      setFailed((f) => ({ ...f, [hole]: true }));
    }
  };

  const finish = async (body: Record<string, unknown>) => {
    setSigning(true);
    setSignError(null);
    try {
      await api(`/v1/golf/scores/${card.score_id}`, { method: 'PATCH', body });
      onChanged();
    } catch (e) {
      setSignError(e as ApiError);
    } finally {
      setSigning(false);
    }
  };

  const par = row?.par ?? 4;
  // Two under to four over, which covers a card without a keypad.
  const options = useMemo(
    () => Array.from({ length: 7 }, (_, i) => par - 2 + i).filter((n) => n >= 1),
    [par],
  );

  return (
    <div className="playcard">
      {/* Whose card. The largest thing on the screen, because a six on
          the wrong person's card is the mistake this has to make hard. */}
      <div className="playcard__who">
        {[
          { score_id: myScoreId, player_name: 'You' },
          ...group.filter((p) => p.score_id !== myScoreId),
        ].map((p) => (
          <button key={p.score_id}
            className={p.score_id === card.score_id ? 'gold small' : 'secondary small'}
            onClick={() => onSwitch(p.score_id)}>
            {p.score_id === myScoreId ? 'You' : p.player_name.split(' ')[0]}
          </button>
        ))}
      </div>

      <Card
        title={card.score_id === myScoreId ? 'Your card' : card.player_name}
        actions={
          <span className="muted small">
            {card.course_name} · {card.tee_name}
            {card.course_handicap !== null && ` · off ${card.course_handicap}`}
          </span>
        }
      >
        <div className="playcard__totals">
          <div>
            <span className="playcard__big">{played.length}</span>
            <span className="muted small"> thru</span>
          </div>
          <div>
            <span className="playcard__big">{grossSoFar || '—'}</span>
            <span className="muted small"> shots</span>
          </div>
          <div>
            <span className="playcard__big">
              {played.length ? toPar(grossSoFar - parSoFar) : '—'}
            </span>
            <span className="muted small"> to par</span>
          </div>
        </div>

        {/* Only when somebody ELSE has been writing on it. Telling a
            member that they have been keeping their own card is noise on
            a screen that has room for none. */}
        {card.marked_by_name && card.marked_by_name !== card.player_name && (
          <p className="hint">{card.marked_by_name} has been keeping this card.</p>
        )}

        {/* The hole. */}
        <div className="hole">
          <div className="hole__nav">
            <button className="secondary" aria-label="Previous hole"
              disabled={hole <= 1} onClick={() => setHole((h) => h - 1)}>‹</button>
            <div className="hole__title">
              <div className="hole__number">{hole}</div>
              <div className="muted small">
                Par {row?.par ?? '—'}
                {row?.stroke_index ? ` · SI ${row.stroke_index}` : ''}
                {row?.strokes_given ? ` · ${row.strokes_given} shot${
                  row.strokes_given === 1 ? '' : 's'}` : ''}
              </div>
            </div>
            <button className="secondary" aria-label="Next hole"
              disabled={hole >= (card.holes ?? 18)}
              onClick={() => setHole((h) => h + 1)}>›</button>
          </div>

          <div className="hole__scores">
            {options.map((n) => (
              <button key={n}
                className={`hole__score ${relative(n, row?.par ?? null)} ${
                  shown === n ? 'hole__score--on' : ''}`}
                onClick={() => save(n)}>
                {n}
              </button>
            ))}
          </div>

          <div className="hole__state">
            {failed[hole] ? (
              // Not hidden and not reverted. They took that score.
              <Status tone="warning">
                Not saved yet — tap it again when you have signal.
              </Status>
            ) : hole in pending ? (
              <span className="muted small">Saving…</span>
            ) : shown !== null ? (
              <span className="muted small">Saved</span>
            ) : (
              <span className="muted small">Tap your score</span>
            )}
            {shown !== null && (
              <button className="secondary small" onClick={() => save(null)}>
                {/* Not a zero. A member who picked up has not scored
                    nothing, they have not finished the hole. */}
                Picked up
              </button>
            )}
          </div>
        </div>

        {/* The end of the round, and the two things that happen at it.
            Signing it in is the player's; attesting is the marker's, and
            the API refuses anybody attesting their own — which is the
            half of the rule software can actually check. */}
        {played.length >= (card.holes ?? 18) && card.status === 'in_progress' && (
          <div className="hole__state" style={{ marginTop: 16 }}>
            <span>
              <strong>{grossSoFar}</strong>
              <span className="muted small"> — that is the round.</span>
            </span>
            <button className="gold" disabled={signing}
              onClick={() => finish(card.score_id === myScoreId
                ? { status: 'complete' } : { attest: true })}>
              {card.score_id === myScoreId ? 'Sign it in' : 'Attest it'}
            </button>
          </div>
        )}
        {played.length >= (card.holes ?? 18) && card.status === 'complete' && (
          <div className="hole__state" style={{ marginTop: 16 }}>
            <Status tone="good">Signed in</Status>
            {card.score_id === myScoreId ? (
              // Still correctable. A card is the club's record until it
              // has gone to the association, and a member who put a five
              // where they meant a six should not have to ring the shop.
              <span className="muted small">
                Tap a hole to correct it while it is still here.
              </span>
            ) : (
              <button className="secondary" disabled={signing}
                onClick={() => finish({ attest: true })}>Attest it</button>
            )}
          </div>
        )}
        {signError && <ErrorBanner error={signError} />}

        {/* The nine little squares, for somebody who wants to see the
            round rather than the hole. Tapping one goes there. */}
        <div className="playcard__strip">
          {holes.map((h) => (
            <button key={h.playing_number}
              className={`strip__box ${h.playing_number === hole ? 'strip__box--on' : ''} ${
                h.strokes === null ? '' : relative(h.strokes, h.par)}`}
              onClick={() => setHole(h.playing_number)}>
              <span className="strip__n">{h.playing_number}</span>
              <span className="strip__v">{h.strokes ?? '·'}</span>
            </button>
          ))}
        </div>
      </Card>

      {payload.competitions.length > 0 && (
        <Card
          title="How It Is Going"
          actions={
            <button className="secondary small" onClick={() => setShowBoard(!showBoard)}>
              {showBoard ? 'Hide' : 'Show the board'}
            </button>
          }
        >
          <p className="muted small">
            {/* Distinct, and counted past three. A member in one competition
                should see its name; a club that ran seven off the same
                morning should not get "Live, Live, Live, Live, Live". */}
            {(() => {
              const names = [...new Set(payload.competitions.map((c) => c.name))];
              return names.length > 3
                ? `${payload.competitions.length} competitions today`
                : names.join(', ');
            })()} — the board moves as cards are kept, so it is what everybody has
            played so far and not a result.
          </p>
          {showBoard && payload.competitions.map((c) => (
            <LiveBoard key={c.competition_id} competitionId={c.competition_id} />
          ))}
        </Card>
      )}
    </div>
  );
}


/* ================================================================== */
/* Where a scan lands                                                  */
/* ================================================================== */

interface ScanResult {
  found: boolean;
  code?: string;
  club?: { id: string; name: string; slug: string };
  course?: { id: string; name: string };
  label?: string;
  starting_hole?: number;
}

/**
 * The screen a QR code opens.
 *
 * Rendered outside the signed-in shell, because a camera cannot present
 * a token and the member may not have opened this app since April. So
 * the order is: say where they are, then ask who they are, then start
 * the card. Saying where they are first is what makes the sign-in worth
 * doing — a bare login screen after scanning a sticker looks broken.
 *
 * What it can say before anybody signs in is deliberately thin (GOLF-21,
 * and routes/scan.ts): the club, the course, and the label on the
 * sticker. Nothing about anybody playing.
 */
export function ScanLandingPage() {
  const { code } = useParams<{ code: string }>();
  const { me, loading } = useSession();
  const navigate = useNavigate();

  const [scan, setScan] = useState<ScanResult | null>(null);
  const [looking, setLooking] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await fetch(
          `${import.meta.env.VITE_API_URL ?? ''}/v1/scan/${encodeURIComponent(code ?? '')}`);
        const body = await r.json().catch(() => null);
        if (live) setScan(body ?? { found: false });
      } catch {
        if (live) setScan({ found: false });
      } finally {
        if (live) setLooking(false);
      }
    })();
    return () => { live = false; };
  }, [code]);

  const startHere = async () => {
    setStarting(true);
    setError(null);
    try {
      await api('/v1/golf/card', { method: 'POST', body: { code } });
      navigate('/golf/card');
    } catch (e) {
      setError(e as ApiError);
      setStarting(false);
    }
  };

  if (looking || loading) {
    return <div className="scan"><Loading rows={3} /></div>;
  }

  if (!scan?.found) {
    return (
      <div className="scan">
        <Card title="That Code Is Not in Use">
          <p className="muted">
            Check the sticker, or ask in the shop. A sticker that has been replaced
            stops working on purpose.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="scan">
      <div className="scan__where">
        <div className="scan__club">{scan.club?.name}</div>
        <div className="scan__label">{scan.label}</div>
        <div className="muted small">
          {scan.course?.name}
          {scan.starting_hole && scan.starting_hole !== 1
            && ` · starting on the ${scan.starting_hole}${
              scan.starting_hole === 2 ? 'nd' : scan.starting_hole === 3 ? 'rd' : 'th'}`}
        </div>
      </div>

      {me ? (
        <Card>
          <ErrorBanner error={error} />
          <p className="muted">
            Keep your own card as you play. Anyone in your group can enter for anybody
            else in it.
          </p>
          <div className="actions">
            <button className="gold" disabled={starting} onClick={startHere}>
              {starting ? 'Starting…' : 'Start my card'}
            </button>
          </div>
        </Card>
      ) : (
        // Where they are first, then who they are. A bare login screen
        // after scanning a sticker looks like the sticker did not work.
        <Login />
      )}
    </div>
  );
}
